import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  BookmarkIcon,
} from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkSolidIcon } from "@heroicons/react/24/solid";
import {
  researchService,
  ResearchNote,
  ResearchNoteType,
  ResearchSourceType,
  ResearchEntityType,
  ResearchVisibility,
  ResearchFilters,
  ResearchNoteCreate,
} from "../services/researchService";
import { useOrganization } from "../context/OrganizationContext";
import toast from "react-hot-toast";
import MarkdownEditor from "../components/markdown/MarkdownEditor";
import MarkdownViewer from "../components/markdown/MarkdownViewer";
import type { MultiEntityMentionsEditorRef } from "../components/MultiEntityMentionsEditor";

const Research: React.FC = () => {
  const { selectedOrg: organization, userRole: userOrgRole } =
    useOrganization();
  const [researchNotes, setResearchNotes] = useState<ResearchNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalNotes, setTotalNotes] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ResearchNote | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [formData, setFormData] = useState<Partial<ResearchNoteCreate>>({
    entity_type: ResearchEntityType.ORGANIZATION,
    entity_id: undefined, // null for organization-level research
    title: "",
    content: "",
    note_type: ResearchNoteType.GENERAL,
    source_type: ResearchSourceType.MANUAL,
    visibility: ResearchVisibility.PUBLIC,
    is_pinned: false,
  });
  const contentEditorRef = useRef<MultiEntityMentionsEditorRef>(null);

  // Filters
  const [filters, setFilters] = useState<ResearchFilters>({
    page: 1,
    page_size: pageSize,
  });

  const canEdit = userOrgRole === "admin" || userOrgRole === "editor";

  // Fetch research notes
  const fetchResearchNotes = useCallback(async () => {
    if (!organization?.id) return;

    setLoading(true);
    try {
      const response = await researchService.listResearchNotes(
        organization.id,
        {
          ...filters,
          search: searchTerm || undefined,
          page: currentPage,
          page_size: pageSize,
        },
      );

      setResearchNotes(response.notes);
      setTotalNotes(response.total);
    } catch (error) {
      console.error("Error fetching research notes:", error);
      toast.error("Failed to load research notes");
    } finally {
      setLoading(false);
    }
  }, [organization?.id, filters, searchTerm, currentPage, pageSize]);

  useEffect(() => {
    fetchResearchNotes();
  }, [fetchResearchNotes]);

  // Toggle pin status
  const togglePin = async (note: ResearchNote) => {
    if (!canEdit) return;

    if (!organization) {
      toast.error("No organization selected");
      return;
    }

    try {
      await researchService.updateResearchNote(organization.id, note.id, {
        is_pinned: !note.is_pinned,
      });
      toast.success(note.is_pinned ? "Unpinned" : "Pinned");
      fetchResearchNotes();
    } catch (error) {
      toast.error("Failed to update pin status");
    }
  };

  // Delete research note
  const deleteNote = async (noteId: string) => {
    if (!canEdit) return;

    if (
      !window.confirm("Are you sure you want to delete this research note?")
    ) {
      return;
    }

    if (!organization) {
      toast.error("No organization selected");
      return;
    }

    try {
      await researchService.deleteResearchNote(organization.id, noteId);
      toast.success("Research note deleted");
      fetchResearchNotes();
      setSelectedNote(null);
    } catch (error) {
      toast.error("Failed to delete research note");
    }
  };

  // Create new research note
  const handleCreate = async () => {
    const tokenizedContent =
      contentEditorRef.current?.getTokenizedContent() || formData.content;

    if (!formData.title || !tokenizedContent) {
      toast.error("Title and content are required");
      return;
    }

    if (!organization) {
      toast.error("No organization selected");
      return;
    }

    const payload = {
      ...formData,
      content: tokenizedContent,
      entity_type: ResearchEntityType.ORGANIZATION,
      entity_id: undefined, // null for organization-level research
      // Ensure we send the enum values (strings), not enum objects
      note_type: formData.note_type || ResearchNoteType.GENERAL,
      source_type: formData.source_type || ResearchSourceType.MANUAL,
      visibility: formData.visibility || ResearchVisibility.PUBLIC,
    };

    try {
      await researchService.createResearchNote(
        organization.id,
        payload as ResearchNoteCreate,
      );

      toast.success("Research note created");
      setShowCreateModal(false);
      setFormData({
        entity_type: ResearchEntityType.ORGANIZATION,
        entity_id: undefined,
        title: "",
        content: "",
        note_type: ResearchNoteType.GENERAL,
        source_type: ResearchSourceType.MANUAL,
        visibility: ResearchVisibility.PUBLIC,
        is_pinned: false,
      });
      fetchResearchNotes();
    } catch (error) {
      toast.error("Failed to create research note");
    }
  };

  // Format entity type for display
  const formatEntityType = (type: ResearchEntityType) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get entity type color
  const getEntityTypeColor = (type: ResearchEntityType) => {
    const colors: Record<ResearchEntityType, string> = {
      [ResearchEntityType.SEGMENT]: "bg-blue-100 text-blue-800",
      [ResearchEntityType.PROPOSITION]: "bg-green-100 text-green-800",
      [ResearchEntityType.POSITION]: "bg-purple-100 text-purple-800",
      [ResearchEntityType.COMPANY]: "bg-orange-100 text-orange-800",
      [ResearchEntityType.CONTACT]: "bg-pink-100 text-pink-800",
      [ResearchEntityType.ORGANIZATION]: "bg-gray-100 text-gray-800",
      [ResearchEntityType.OKR]: "bg-indigo-100 text-indigo-800",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  // Get note type icon
  const getNoteTypeIcon = (type: ResearchNoteType) => {
    switch (type) {
      case ResearchNoteType.INSIGHT:
        return "💡";
      case ResearchNoteType.RESEARCH:
        return "🔬";
      case ResearchNoteType.MEETING:
        return "🤝";
      case ResearchNoteType.ANALYSIS:
        return "📊";
      case ResearchNoteType.RECOMMENDATION:
        return "📝";
      default:
        return "📄";
    }
  };

  const totalPages = Math.ceil(totalNotes / pageSize);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Research Library
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {totalNotes} research {totalNotes === 1 ? "note" : "notes"}
              </p>
            </div>

            <div className="flex items-center space-x-3">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`px-3 py-1 rounded ${
                    viewMode === "grid"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Grid
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`px-3 py-1 rounded ${
                    viewMode === "list"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  List
                </button>
              </div>

              {canEdit && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  New Research
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search research notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-4 py-2 border rounded-lg ${
                showFilters
                  ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <FunnelIcon className="w-5 h-5 mr-2" />
              Filters
              {Object.keys(filters).filter(
                (key) => key !== "page" && key !== "page_size",
              ).length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                  {
                    Object.keys(filters).filter(
                      (key) => key !== "page" && key !== "page_size",
                    ).length
                  }
                </span>
              )}
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Entity Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entity Type
                  </label>
                  <select
                    value={filters.entity_type || ""}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        entity_type:
                          (e.target.value as ResearchEntityType) || undefined,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Types</option>
                    {Object.values(ResearchEntityType).map((type) => (
                      <option key={type} value={type}>
                        {formatEntityType(type)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Note Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Note Type
                  </label>
                  <select
                    value={filters.note_type || ""}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        note_type:
                          (e.target.value as ResearchNoteType) || undefined,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Note Types</option>
                    {Object.values(ResearchNoteType).map((type) => (
                      <option key={type} value={type}>
                        {getNoteTypeIcon(type)}{" "}
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Source Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Source
                  </label>
                  <select
                    value={filters.source_type || ""}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        source_type:
                          (e.target.value as ResearchSourceType) || undefined,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Sources</option>
                    {Object.values(ResearchSourceType).map((type) => (
                      <option key={type} value={type}>
                        {type
                          .replace(/_/g, " ")
                          .toLowerCase()
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Pinned Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={
                      filters.is_pinned === true
                        ? "pinned"
                        : filters.is_pinned === false
                          ? "unpinned"
                          : ""
                    }
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        is_pinned:
                          e.target.value === "pinned"
                            ? true
                            : e.target.value === "unpinned"
                              ? false
                              : undefined,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Notes</option>
                    <option value="pinned">Pinned Only</option>
                    <option value="unpinned">Unpinned Only</option>
                  </select>
                </div>
              </div>

              {/* Clear Filters */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() =>
                    setFilters({ page: currentPage, page_size: pageSize })
                  }
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : researchNotes.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No research notes found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ||
              Object.keys(filters).filter(
                (key) => key !== "page" && key !== "page_size",
              ).length > 0
                ? "Try adjusting your filters or search term"
                : "Get started by creating a new research note"}
            </p>
            {canEdit &&
              !searchTerm &&
              Object.keys(filters).filter(
                (key) => key !== "page" && key !== "page_size",
              ).length === 0 && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Create First Research Note
                  </button>
                </div>
              )}
          </div>
        ) : viewMode === "grid" ? (
          // Grid View
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {researchNotes.map((note) => (
              <div
                key={note.id}
                className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedNote(note)}
              >
                <div className="p-4">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-3">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEntityTypeColor(
                        note.entity_type,
                      )}`}
                    >
                      {formatEntityType(note.entity_type)}
                      {note.entity_name && `: ${note.entity_name}`}
                    </span>
                    {canEdit && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePin(note);
                        }}
                        className="text-gray-400 hover:text-yellow-500"
                      >
                        {note.is_pinned ? (
                          <BookmarkSolidIcon className="w-5 h-5 text-yellow-500" />
                        ) : (
                          <BookmarkIcon className="w-5 h-5" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    <span className="mr-2">
                      {getNoteTypeIcon(note.note_type)}
                    </span>
                    {note.title}
                  </h3>

                  {/* Content Preview */}
                  <div className="text-sm text-gray-600 line-clamp-3 mb-3">
                    <MarkdownViewer content={note.content} />
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center">
                      <UserIcon className="w-4 h-4 mr-1" />
                      {note.created_by_name || "Unknown"}
                    </div>
                    <div className="flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-1" />
                      {formatDate(note.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // List View
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Author
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {researchNotes.map((note) => (
                  <tr
                    key={note.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedNote(note)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {note.is_pinned && (
                          <BookmarkSolidIcon className="w-4 h-4 text-yellow-500 mr-2" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {note.title}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-md">
                            {note.content}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEntityTypeColor(
                          note.entity_type,
                        )}`}
                      >
                        {formatEntityType(note.entity_type)}
                        {note.entity_name && `: ${note.entity_name}`}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="mr-1">
                        {getNoteTypeIcon(note.note_type)}
                      </span>
                      {note.note_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {note.created_by_name || "Unknown"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(note.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {canEdit && (
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePin(note);
                            }}
                            className="text-gray-400 hover:text-yellow-500"
                          >
                            {note.is_pinned ? (
                              <BookmarkSolidIcon className="w-5 h-5 text-yellow-500" />
                            ) : (
                              <BookmarkIcon className="w-5 h-5" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNote(note.id);
                            }}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {(currentPage - 1) * pageSize + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalNotes)}
                  </span>{" "}
                  of <span className="font-medium">{totalNotes}</span> results
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNumber = i + 1;
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => setCurrentPage(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNumber
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Note Detail Modal */}
      {selectedNote && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => setSelectedNote(null)}
            />
            <div className="relative bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {selectedNote.title}
                    </h2>
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEntityTypeColor(
                          selectedNote.entity_type,
                        )}`}
                      >
                        {formatEntityType(selectedNote.entity_type)}
                        {selectedNote.entity_name &&
                          `: ${selectedNote.entity_name}`}
                      </span>
                      <span>
                        {getNoteTypeIcon(selectedNote.note_type)}{" "}
                        {selectedNote.note_type}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedNote(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="px-6 py-4">
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                  <div>
                    <span className="text-gray-500">Created by:</span>{" "}
                    <span className="text-gray-900">
                      {selectedNote.created_by_name || "Unknown"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Created:</span>{" "}
                    <span className="text-gray-900">
                      {formatDate(selectedNote.created_at)}
                    </span>
                  </div>
                  {selectedNote.updated_by && (
                    <>
                      <div>
                        <span className="text-gray-500">Updated by:</span>{" "}
                        <span className="text-gray-900">
                          {selectedNote.updated_by_name || "Unknown"}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Updated:</span>{" "}
                        <span className="text-gray-900">
                          {formatDate(selectedNote.updated_at)}
                        </span>
                      </div>
                    </>
                  )}
                  <div>
                    <span className="text-gray-500">Source:</span>{" "}
                    <span className="text-gray-900">
                      {selectedNote.source_type
                        .replace(/_/g, " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="prose max-w-none">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    Content
                  </h3>
                  <div className="text-gray-700">
                    <MarkdownViewer content={selectedNote.content} />
                  </div>
                </div>

                {/* Attachments */}
                {selectedNote.attachments.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Attachments ({selectedNote.attachments.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedNote.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="flex items-center p-3 bg-gray-50 rounded-lg"
                        >
                          <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-3" />
                          <div className="flex-1">
                            <p className="text-sm text-gray-900">
                              {attachment.attachment_type}
                            </p>
                            {attachment.attachment_type === "url" &&
                              attachment.attachment_data.url && (
                                <a
                                  href={attachment.attachment_data.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-700"
                                >
                                  {attachment.attachment_data.url}
                                </a>
                              )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {canEdit && (
                  <div className="mt-6 pt-4 border-t flex justify-end space-x-3">
                    <button
                      onClick={() => deleteNote(selectedNote.id)}
                      className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => setShowCreateModal(false)}
            />
            <div className="relative bg-white rounded-lg max-w-2xl w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">
                  Add Organization Research Note
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter research title..."
                  />
                </div>

                {/* Note Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={formData.note_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        note_type: e.target.value as ResearchNoteType,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.values(ResearchNoteType).map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <MarkdownEditor
                    ref={contentEditorRef}
                    value={formData.content || ""}
                    onChange={(value) =>
                      setFormData({ ...formData, content: value })
                    }
                    placeholder="Enter research content... Type @ to mention OKRs, Segments, Propositions, or Companies"
                    organizationId={organization?.id || ""}
                  />
                </div>

                {/* Pin */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="pin-note"
                    checked={formData.is_pinned}
                    onChange={(e) =>
                      setFormData({ ...formData, is_pinned: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="pin-note"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Pin this note to the top
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Research Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Research;
